import PractitionerSearch from "../../src/practitioner-search/PractitionerSearch";
import { getCounsellingMetaFromSearch } from "../../src/practitioner-search/counsellingMeta";
import { GoogleMapProvider } from "../../src/utilities/contexts";
import { getData, postData } from "../../src/utilities/axios";
import Head from "next/head";

const latLongMap = {
  brighton: { lat: 50.82838, lng: -0.13947 },
  london: { lat: 51.50735, lng: -0.12776 },
  bristol: { lat: 51.4545, lng: -2.58791 },
  swindon: { lat: 51.5686, lng: -1.77216 },
  bournemouth: { lat: 50.71556, lng: -1.87576 },
  portsmouth: { lat: 50.81977, lng: -1.08821 },
  plymouth: { lat: 50.3755, lng: -4.14266 },
  glasgow: { lat: 55.86424, lng: -4.2518 },
  exeter: { lat: 50.72006, lng: -3.53347 },
  manchester: { lat: 53.48076, lng: -2.24263 },
  liverpool: { lat: 53.40837, lng: -2.99157 },
  birmingham: { lat: 52.48624, lng: -1.8904 },
  oxford: { lat: 51.75202, lng: -1.25773 },
  southampton: { lat: 50.9097, lng: -1.40435 },
  bath: { lat: 51.38113, lng: -2.35909 },
  reading: { lat: 51.45625, lng: -0.97113 },
  cardiff: { lat: 51.48158, lng: -3.17909 },
  swansea: { lat: 51.62144, lng: -3.94365 },
  leeds: { lat: 53.80075, lng: -1.54908 },
  cambridge: { lat: 52.20527, lng: 0.12182 },
  sheffield: { lat: 53.38113, lng: -1.47009 },
  nottingham: { lat: 52.95478, lng: -1.15809 },
  belfast: { lat: 54.59729, lng: -5.93012 },
  ealing: { lat: 51.5138, lng: -0.30144 },
  bromley: { lat: 51.403, lng: 0.0155 },
  clapham: { lat: 51.46261, lng: -0.13843 },
  islington: { lat: 51.53805, lng: -0.10308 },
  croydon: { lat: 51.37208, lng: -0.11047 },
  wimbledon: { lat: 51.4212, lng: -0.21007 },
  hackney: { lat: 51.545, lng: -0.055 },
  twickenham: { lat: 51.45599, lng: -0.34233 },
  richmond: { lat: 51.4617, lng: -0.3063 },
  shoreditch: { lat: 51.5348, lng: -0.0874 },
  fulham: { lat: 51.47871, lng: -0.2017 },
  harrow: { lat: 51.58302, lng: -0.33782 },
  balham: { lat: 51.4434, lng: -0.1525 },
  lambeth: { lat: 51.49635, lng: -0.11152 },
  camden: { lat: 51.53945, lng: -0.14244 },
  stratford: { lat: 51.54, lng: -0.003 },
  pimlico: { lat: 51.49, lng: -0.137 },
  tottenham: { lat: 51.6033, lng: -0.0659 },
  dulwich: { lat: 51.444, lng: -0.0865 },
  hounslow: { lat: 51.47, lng: -0.36 },
  angel: { lat: 51.5322, lng: -0.1065 },
  hammersmith: { lat: 51.495, lng: -0.225 },
  surbiton: { lat: 51.389, lng: -0.3008 },
  dalston: { lat: 51.545, lng: -0.071 },
  lewisham: { lat: 51.445, lng: -0.0198 },
  peckham: { lat: 51.4745, lng: -0.0641 },
  battersea: { lat: 51.4731, lng: -0.1505 },
  guildford: { lat: 51.2362, lng: 0.5704 },
  "notting-hill": { lat: 51.516, lng: 0.209 },
  putney: { lat: 51.4596, lng: 0.2141 },
  chelsea: { lat: 51.4869, lng: 0.17 },
};

const canonicalLocationMap = {
  // Named location aliases
  "brighton-place": "brighton",
  "brighton-and-hove": "brighton",
  "hampstead-green": "hampstead",
  "hampstead-grove": "hampstead",
  "hampstead-square": "hampstead",
  "hampstead-gate": "hampstead",
  "hackney-downs": "hackney",
  "hackney-central": "hackney",
  "hackney-wick": "hackney",
  "hackney-grove": "hackney",
  "hackney-walk": "hackney",

  // Postcode → named location canonicals (location.xml pages only)
  // Pages still render normally for postcode URLs; this only sets the
  // <link rel="canonical"> so Google consolidates ranking to the named page.

  // London (central)
  ec1: "london",
  ec2: "london",
  ec3: "london",
  ec4: "london",
  wc1: "london",
  wc2: "london",

  // Brighton
  bn1: "brighton",
  bn2: "brighton",
  bn3: "brighton",

  // Manchester
  m1: "manchester",
  m2: "manchester",

  // Liverpool
  l1: "liverpool",
  l2: "liverpool",

  // Birmingham
  b1: "birmingham",
  b2: "birmingham",

  // Hackney
  e5: "hackney",
  e8: "hackney",
  e9: "hackney",

  // Hampstead
  nw3: "hampstead",

  // Bristol
  bs1: "bristol",
  bs2: "bristol",

  // York
  yo1: "york",
  yo10: "york",

  // Cambridge
  cb1: "cambridge",
  cb2: "cambridge",

  // South East London
  se1: "south-east-london",
  se5: "south-east-london",
  se6: "south-east-london",
  se8: "south-east-london",
  se10: "south-east-london",
  se13: "south-east-london",
  se15: "south-east-london",
  se16: "south-east-london",
  se22: "south-east-london",
  se23: "south-east-london",
  se24: "south-east-london",

  // Ilford
  ig1: "ilford",
  ig2: "ilford",

  // Belfast
  bt1: "belfast",
  bt2: "belfast",

  // Plymouth
  pl1: "plymouth",
  pl2: "plymouth",

  // Kingston upon Thames
  kt1: "kingston-upon-thames",
  kt2: "kingston-upon-thames",

  // Cardiff
  cf10: "cardiff",
  cf11: "cardiff",

  // Watford
  wd17: "watford",
  wd18: "watford",

  // Sheffield
  s1: "sheffield",
  s2: "sheffield",

  // Leeds
  ls1: "leeds",
  ls2: "leeds",

  // Glasgow
  g1: "glasgow",
  g2: "glasgow",

  // Edinburgh
  eh1: "edinburgh",
  eh2: "edinburgh",

  // Preston
  pr1: "preston",
  pr2: "preston",

  // Oxford
  ox1: "oxford",
  ox2: "oxford",

  // Tunbridge Wells
  tn1: "tunbridge-wells",
  tn2: "tunbridge-wells",
  tn4: "tunbridge-wells",

  // Milton Keynes
  mk1: "milton-keynes",
  mk9: "milton-keynes",

  // Portsmouth
  po1: "portsmouth",
  po2: "portsmouth",

  // Whitstable
  ct5: "whitstable",
};

const normalizeSlug = (value) =>
  typeof value === "string"
    ? value
        .toLowerCase()
        .trim()
        .replace(/[\s-]+/g, "-")
    : "";

const VALID_SLUG_SEGMENT = /^[a-zA-Z0-9][a-zA-Z0-9()-]*$/;
const MAX_SEARCH_SEGMENTS = 2;

const SearchPractitionerLayout = (props) => {
  const canonicalPath = props.canonicalPath || "/counselling";
  const canonicalUrl = `https://welldoing.org${canonicalPath}`;

  const { title, description } =
    props.pageMeta ??
    getCounsellingMetaFromSearch(props.search, props.canonicalPath);

  return (
    <GoogleMapProvider>
      <Head>
        <link rel="canonical" href={canonicalUrl} />
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="og:description" content={description} />
      </Head>
      <PractitionerSearch {...props} />
    </GoogleMapProvider>
  );
};

/**
 *
 * @param {import('next').GetStaticPropsContext} context
 * @returns
 */
export async function getStaticProps(context) {
  const props = {
    pageMeta: null,
    canonicalPath: "/counselling",
    search: {
      location: {
        outcode: null,
        place: null,
      },
      modality: null,
      original: [],
    },
    serverResults: [],
  };
  const searchPathParts = context.params?.search ?? [];

  if (
    searchPathParts.length > MAX_SEARCH_SEGMENTS ||
    searchPathParts.some((seg) => !VALID_SLUG_SEGMENT.test(seg))
  ) {
    return { notFound: true };
  }

  const canonicalSegments = searchPathParts
    .map((part) => normalizeSlug(part))
    .filter(Boolean);

  if (canonicalSegments.length > 0) {
    canonicalSegments[0] =
      canonicalLocationMap[canonicalSegments[0]] || canonicalSegments[0];
    props.canonicalPath = `/counselling/${canonicalSegments.join("/")}`;
  }
  const convertArray = (arr, arrLabels = []) =>
    arr.map((elem) => ({
      ...elem,
      selected: arrLabels.includes(elem.label),
    }));
  const utilData = await getData(
    `${process.env.NEXT_PUBLIC_NEW_UTILITIES_URL}utilities`,
    process.env.NEXT_PUBLIC_NEW_UTILITIES_APIKEY,
  );
  props.search = {
    ...props.search,
    ...utilData,
    clientGroups: utilData.clientGroups.map((elem) => ({
      ...elem,
      selected: false,
    })),
    gender: utilData.genders.map((elem) => ({
      ...elem,
      selected: false,
    })),
    concessions: utilData.concessions.map((elem) => ({
      ...elem,
      selected: false,
    })),
    practiceAreas: utilData.therapistPracticeAreas.map((elem) => ({
      ...elem,
      selected: false,
    })),
    practitionerModalities: utilData.therapistPractitionerModalities.map(
      (elem) => ({
        ...elem,
        selected: false,
      }),
    ),
    // Default english to selected
    languages: convertArray(utilData.languages),
    // Default online to selected
    sessionTypes: convertArray(utilData.sessionTypes),
    availability: [
      [false, false, false, false, false, false, false],
      [false, false, false, false, false, false, false],
      [false, false, false, false, false, false, false],
      [false, false, false, false, false, false, false],
      [false, false, false, false, false, false, false],
    ],
  };
  // const searchSeed = Math.floor(Math.random() * 10000);
  let newQuery = {
    practitionerType: "therapist",
    resultIndex: 0,
    resultItems: 20,
    ignoreUserId: null,
    orderBy: {
      ascending: true,
      value: null,
      seed: 3135,
    },
  };

  if (context.params?.search) {
    const norm = (string) => string.toLowerCase().replace(/[\s-]+/g, "-");
    const mapBySlug = Object.fromEntries(
      Object.entries(latLongMap).map(([k, v]) => [norm(k), v]),
    );
    const key = norm(context.params.search[0]);

    if (mapBySlug[key]) {
      newQuery = {
        ...newQuery,
        distance: 1000000,
        location: mapBySlug[key],
        orderBy: {
          ascending: true,
          value: "distance",
          seed: 3135,
        },
      };
    }
  }

  const { results: therapistRes } = await postData(
    null,
    {
      ...newQuery,
    },
    `${process.env.NEXT_PUBLIC_NEW_SEARCH_URL}search`,
    process.env.NEXT_PUBLIC_NEW_SEARCH_APIKEY,
  );
  props.serverResults = therapistRes;
  if (!context.params?.search) {
    props.pageMeta = getCounsellingMetaFromSearch(
      props.search,
      props.canonicalPath,
    );
    return {
      props,
    };
  }
  const { search } = context.params;
  const allPracticeModalitiesLower =
    utilData.therapistPractitionerModalities.map((area) =>
      area.label.toLowerCase(),
    );
  props.search.original = search;
  const [location, modality] = search;
  const postcodeRegex = /^([A-Z]{1,2}[0-9][A-Z0-9]?)( ?[0-9][A-Z]{2})?$/i;
  let decodedLocation = location;
  if (location)
    try {
      decodedLocation = decodeURIComponent(location || "");
    } catch {
      decodedLocation = location;
    }
  let decodedModality = modality;
  if (modality)
    try {
      decodedModality = decodeURIComponent(modality || "");
    } catch {
      decodedModality = modality;
    }
  const postcodeMatch = decodedLocation
    ? decodedLocation.match(postcodeRegex)
    : null;
  const locationKey = decodedLocation
    ? decodedLocation.toLowerCase().replace(/-/g, " ")
    : "";
  if (postcodeMatch) {
    props.search.location.outcode = postcodeMatch[0];
  } else if (
    decodedLocation &&
    !allPracticeModalitiesLower.includes(locationKey)
  ) {
    props.search.location.place = decodedLocation.replace(/-/g, " ");
  }
  if (modality) {
    props.search.modality = decodedModality.toLowerCase().replace(/-/g, " ");
  } else if (allPracticeModalitiesLower.includes(locationKey)) {
    props.search.modality = locationKey;
  }
  const { results: newTherapistRes } = await postData(
    null,
    {
      ...newQuery,
    },
    `${process.env.NEXT_PUBLIC_NEW_SEARCH_URL}search`,
    process.env.NEXT_PUBLIC_NEW_SEARCH_APIKEY,
  );
  props.serverResults = newTherapistRes;
  props.pageMeta = getCounsellingMetaFromSearch(
    props.search,
    props.canonicalPath,
  );
  return {
    props,
  };
}

export async function getStaticPaths() {
  const locations = [
    "brighton",
    "london",
    "bristol",
    "swindon",
    "bournemouth",
    "portsmouth",
    "plymouth",
    "glasgow",
    "exeter",
    "manchester",
    "liverpool",
    "birmingham",
    "oxford",
    "southampton",
    "bath",
    "reading",
    "cardiff",
    "swansea",
    "leeds",
    "cambridge",
    "sheffield",
    "nottingham",
    "belfast",
    "ealing",
    "bromley",
    "clapham",
    "islington",
    "croydon",
    "wimbledon",
    "hackney",
    "twickenham",
    "richmond",
    "shoreditch",
    "fulham",
    "harrow",
    "westminster",
    "balham",
    "lambeth",
    "camden",
    "stratford",
    "pimlico",
    "tottenham",
    "dulwich",
    "hounslow",
    "angel",
    "hammersmith",
    "brixton",
    "walthamstow",
    "marylebone",
    "surbiton",
    "dalston",
    "lewisham",
    "peckham",
    "battersea",
    "guildford",
    "notting-hill",
    "putney",
    "chelsea",
  ];

  return {
    paths: locations.map((location) => ({
      params: {
        search: [location],
      },
    })),
    fallback: "blocking",
  };
}

export default SearchPractitionerLayout;
